// systems/experience.js

import { XP_TABLE, GAME_CONFIG } from '../game/constants.js';
import { gameState } from '../game/gameState.js';
import { IS_DEV } from '../config/env.js';
import { adminPanel } from '../skills/admin/admin.js';
import { showXPGain } from '../ui/floatingNotifications.js';
import { rollForCorruption, getCorruptionXPMultiplier } from './corruption.js';
import { getTalentXPBonus } from './talentTree/talentTree.js';

export function getLevelFromXP(xp) {
    for (let level = GAME_CONFIG.MAX_LEVEL; level >= 1; level--) {
        if (xp >= XP_TABLE[level - 1]) {
            return level;
        }
    }
    return 1;
}

export function getVirtualLevelFromXP(xp) {
    for (let level = GAME_CONFIG.VIRTUAL_LEVEL_CAP; level >= 1; level--) {
        if (XP_TABLE[level - 1] !== undefined && xp >= XP_TABLE[level - 1]) {
            return level;
        }
    }
    return getLevelFromXP(xp);
}

export function getXPToNextLevel(currentXP) {
    const level = getLevelFromXP(currentXP);
    if (level >= GAME_CONFIG.MAX_LEVEL) return 0;
    return XP_TABLE[level] - currentXP;
}

export function getXPToNextVirtualLevel(currentXP) {
    const virtualLevel = getVirtualLevelFromXP(currentXP);
    if (virtualLevel >= GAME_CONFIG.VIRTUAL_LEVEL_CAP) return 0;
    return XP_TABLE[virtualLevel] - currentXP;
}

export function getLevelProgress(currentXP) {
    const level = getLevelFromXP(currentXP);
    if (level >= GAME_CONFIG.MAX_LEVEL) return 1;

    const currentLevelXP = XP_TABLE[level - 1];
    const nextLevelXP = XP_TABLE[level];
    return (currentXP - currentLevelXP) / (nextLevelXP - currentLevelXP);
}

export function getVirtualLevelProgress(currentXP) {
    const virtualLevel = getVirtualLevelFromXP(currentXP);
    if (virtualLevel >= GAME_CONFIG.VIRTUAL_LEVEL_CAP) return 1;

    const currentLevelXP = XP_TABLE[virtualLevel - 1];
    const nextLevelXP = XP_TABLE[virtualLevel];
    return (currentXP - currentLevelXP) / (nextLevelXP - currentLevelXP);
}

export function isMaxLevel(skillId) {
    const skill = gameState.getSkill(skillId);
    return skill.level >= GAME_CONFIG.MAX_LEVEL;
}

export function isMaxVirtualLevel(skillId) {
    const skill = gameState.getSkill(skillId);
    return skill.virtualLevel >= GAME_CONFIG.VIRTUAL_LEVEL_CAP;
}

/**
 * Check if a skill has reached mastery (virtual level 120)
 * @param {string} skillId - The skill to check
 * @returns {boolean} True if skill is at level 120
 */
export function hasMastery(skillId) {
    const skill = gameState.getSkill(skillId);
    return skill?.virtualLevel >= 120;
}

/**
 * Get the mastery multiplier for a skill
 * At level 120, all tool and talent effects are doubled
 * @param {string} skillId - The skill to check
 * @returns {number} 2.0 if mastery, 1.0 otherwise
 */
export function getMasteryMultiplier(skillId) {
    return hasMastery(skillId) ? 2.0 : 1.0;
}

/**
 * Apply mastery bonus to a multiplier value
 * Doubles the BONUS portion (e.g., 1.5x becomes 2.0x: 1 + (0.5 * 2) = 2.0)
 * @param {string} skillId - The skill to check
 * @param {number} baseMultiplier - The base multiplier (e.g., 1.5 for 50% bonus)
 * @returns {number} The mastery-adjusted multiplier
 */
export function applyMasteryToMultiplier(skillId, baseMultiplier) {
    if (!hasMastery(skillId)) return baseMultiplier;
    // Double the bonus portion: 1 + (bonus * 2)
    const bonus = baseMultiplier - 1;
    return 1 + (bonus * 2);
}

export function getXPPerHour(skillId) {
    const skill = gameState.getSkill(skillId);
    if (!skill.xpHistory || skill.xpHistory.length === 0) return 0;

    const now = Date.now();
    const recentXP = skill.xpHistory.filter(e => now - e.time <= 3600000);
    if (recentXP.length === 0) return 0;

    const totalXP = recentXP.reduce((sum, e) => sum + e.amount, 0);
    const oldestEntry = recentXP[0].time;
    const timeSpanMs = now - oldestEntry;

    if (timeSpanMs < 1000) return 0;

    return Math.round((totalXP / timeSpanMs) * 3600000);
}

/**
 * Add XP to a skill
 * @param {string} skillId - The skill to add XP to
 * @param {number} amount - Base XP amount
 * @param {string|null} nodeId - Optional node ID for admin panel tracking
 * @param {object} options - Optional settings
 * @param {boolean} options.skipTracking - Skip xpHistory tracking (for offline progress)
 * @param {boolean} options.skipCorruption - Skip corruption roll (for passive XP like pet contracts)
 */
export function addXP(skillId, amount, nodeId = null, options = {}) {
    const { skipTracking = false, skipCorruption = false } = options;
    const skill = gameState.getSkill(skillId);

    // Apply admin XP multiplier in dev mode
    let finalAmount = amount;
    if (IS_DEV && adminPanel) {
        finalAmount = adminPanel.getEffectiveXp(skillId, nodeId, amount);
    }

    // Apply corruption XP multiplier (only floor if corruption is active)
    const corruptionXPMult = getCorruptionXPMultiplier(skillId);
    if (corruptionXPMult > 1) {
        finalAmount = Math.floor(finalAmount * corruptionXPMult);
    }

    // Apply talent XP bonus (multiplicative)
    const talentXPBonus = getTalentXPBonus(skillId);
    if (talentXPBonus > 0) {
        finalAmount = Math.floor(finalAmount * (1 + talentXPBonus));
    }

    const oldLevel = skill.level;
    const oldXP = skill.xp;

    skill.xp += finalAmount;

    const realLevel = getLevelFromXP(skill.xp);
    const virtualLevel = getVirtualLevelFromXP(skill.xp);

    skill.level = realLevel;
    skill.virtualLevel = virtualLevel;

    // Calculate progress based on virtual level for proper tracking
    const displayLevel = virtualLevel;
    const currentLevelXP = XP_TABLE[displayLevel - 1];
    const nextLevelXP = XP_TABLE[displayLevel] ?? XP_TABLE[GAME_CONFIG.VIRTUAL_LEVEL_CAP - 1];

    skill.currentXp = skill.xp - currentLevelXP;
    skill.nextLevelXp = nextLevelXP - currentLevelXP;

    // Track XP for XP/hour calculation (skip for offline progress to avoid spiking)
    if (!skipTracking) {
        if (!skill.xpHistory) {
            skill.xpHistory = [];
        }

        const now = Date.now();
        skill.xpHistory.push({ amount: finalAmount, time: now });
        skill.xpHistory = skill.xpHistory.filter(e => now - e.time <= 3600000);
    }

    gameState.recalculateTotalLevel();

    // Show floating XP notification (skip if high speed mode)
    const speedMult = IS_DEV ? adminPanel.state.global.speedMultiplier : 1;
    if (speedMult <= 2) {
        showXPGain(skillId, finalAmount);
    }

    // Roll for corruption proc on action completion (skip at high speeds, skip for passive XP)
    // Combat uses its own per-attack corruption roll in combat.js
    if (speedMult <= 2 && !skipCorruption && skillId !== 'combat') {
        rollForCorruption(skillId);
    }

    return {
        skillId,
        xpGained: finalAmount,
        oldXP,
        newXP: skill.xp,
        oldLevel,
        newLevel: realLevel,
        leveledUp: realLevel > oldLevel,
        levelsGained: realLevel - oldLevel,
    };
}

/**
 * Calculate scaled Architecting XP for chest claims
 * Scales with Architecting level: base * (1 + level * 0.04)
 * With base 150: Level 1 ~156, Level 50 ~450, Level 99 ~744
 * Targets ~40% of blueprint XP/hr as passive income
 * @param {number} baseXP - The base XP amount
 * @returns {number} Scaled XP amount (rounded)
 */
export function getScaledArchitectingXP(baseXP) {
    const archSkill = gameState.getSkill('architecting');
    const archLevel = archSkill?.level || 1;
    const multiplier = 1 + (archLevel * 0.04);
    return Math.round(baseXP * multiplier);
}

export default {
    getLevelFromXP,
    getVirtualLevelFromXP,
    getXPToNextLevel,
    getXPToNextVirtualLevel,
    getLevelProgress,
    getVirtualLevelProgress,
    isMaxLevel,
    isMaxVirtualLevel,
    hasMastery,
    getMasteryMultiplier,
    applyMasteryToMultiplier,
    getXPPerHour,
    addXP,
    getScaledArchitectingXP,
};
